'use client';

/**
 * AddVendorCard — full-bleed Add Vendor form (canvas).
 *
 * Mode4-native parallel form. Mounts when the explorer fires the
 * `add_vendor` selection. MVP scaffold — the form fields land on
 * top of this card; submit fires `vendor_portal.create` via the
 * action gateway.
 *
 * Cards-everywhere doctrine: no imports of legacy add-vendor
 * widgets. Form-state lives inside this component.
 *
 * @see klyde_edge/ui_config/composition/cards/vendors.py — content_key="add_vendor"
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useState } from 'react';
import { UserPlus, Loader2, X } from 'lucide-react';
import { useLayoutStore } from '@/lib/stores/layout-store';
import { text, surfaceBase } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';

export function AddVendorCard() {
  const setExplorerSelection = useLayoutStore((s) => s.setExplorerSelection);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const exit = () => setExplorerSelection(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    // TODO: wire vendor_portal.create action gateway call once the
    // action lands. MVP scaffold only — clears back to the catalog.
    await new Promise((r) => setTimeout(r, 250));
    setSubmitting(false);
    exit();
  };

  return (
    <div className="h-full flex flex-col" style={{ fontSize: surfaceBase.canvas }}>
      <header className="flex items-center gap-2 px-4 py-3 border-b border-border/60">
        <UserPlus className="h-4 w-4 text-muted-foreground" />
        <h2 className={cn(text.heading, 'flex-1')}>Add Vendor</h2>
        <button
          type="button"
          onClick={exit}
          className="p-1 rounded hover:bg-accent/50 text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Cancel"
        >
          <X className="h-4 w-4" />
        </button>
      </header>

      <div className="flex-1 overflow-auto p-6">
        <form onSubmit={handleSubmit} className="max-w-md space-y-4">
          <label className="block">
            <span className={cn(text.muted, 'font-medium')}>Name</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className={cn(text.body, 'mt-1 w-full px-3 py-2 border border-border rounded-md bg-background')}
            />
          </label>

          <label className="block">
            <span className={cn(text.muted, 'font-medium')}>Email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={cn(text.body, 'mt-1 w-full px-3 py-2 border border-border rounded-md bg-background')}
            />
          </label>

          <div className="flex items-center gap-2">
            <button
              type="submit"
              disabled={submitting || !name.trim()}
              className={cn(text.body, 'px-4 py-2 font-medium bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 inline-flex items-center gap-2')}
            >
              {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Create vendor
            </button>
            <button
              type="button"
              onClick={exit}
              disabled={submitting}
              className={cn(text.body, 'px-4 py-2 font-medium border border-border rounded-md hover:bg-accent/50 disabled:opacity-50')}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
